import {Component, effect, input, OnInit, output} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {SelectionZoneService} from './service/selection-zone.service';
import {FormsModule} from '@angular/forms';
import {DropdownModule} from 'primeng/dropdown';
import {SelectItemGroup} from 'primeng/api';
import {AuthService} from '../../services/auth-service.service';
import {Mode} from './model/mode.enum';
import {ButtonModule} from 'primeng/button';
import {NgClass} from '@angular/common';
import {finalize} from 'rxjs/operators';

@Component({
	selector: 'app-selection-zone',
	templateUrl: './selection-zone.component.html',
	styleUrls: ['./selection-zone.component.scss'],
	standalone: true,
	imports: [FormsModule, DropdownModule, ButtonModule, TranslateModule, NgClass]
})
export class SelectionZoneComponent implements OnInit {

	// Section input & output
	// Permet de présélectionner une zone au chargement du composant (ex pour l'écran équipe ou groupe de travail). Il doit être undefined pour l'écran de sélection d'un bureau
	mode = input.required<Mode>();
	loading = input<boolean>();
	zoneSelectionneeId = input<number>();
	afficherTitre = input<boolean>(true);
	notifierZoneSelectionneeId = output<number>();
	// fin section input & output

	// Section variables bindées composant
	groupesZones: SelectItemGroup[];
	_zoneSelectionneeId: number | null = null;
	_loading = false;
	// fin section variables bindées composant

	dispositionLabel: string;

	private loginUtilisateurConnecte: string;

	constructor(private authService: AuthService, private selectionZoneService: SelectionZoneService, private translateService: TranslateService) {
		this.loginUtilisateurConnecte = this.authService.getUtilisateurConnecte().login;

		effect(() => {
			// à chaque modification de l'input zone selectionneeId, on répercute sur le binding du composant
			if (this.mode() === Mode.Modification && this.zoneSelectionneeId() !== this._zoneSelectionneeId) {
				this._zoneSelectionneeId = this.zoneSelectionneeId();
			}
		});
		effect(() => {
			this._loading = this.loading();
		});
	}

	ngOnInit(): void {
		// si on est en mode modification, on préremplit la zone sélectionnée avec la zoneSelectionneeId passée en input
		// c'est le cas pour l'écran des préférences utilisateurs car on doit présélectionner la zone de travail par défaut de l'utilsisateur
		this._zoneSelectionneeId = this.mode() === Mode.Modification ? this.zoneSelectionneeId() : null;

		this.dispositionLabel = this.mode() === Mode.Selection ? 'row-label' : 'column-label';

		this.recupererSitesEtZonesAutorisees();
	}

	/**
	 * Récupère les sites et zones autorisés de l'utilisateur.
	 */
	recupererSitesEtZonesAutorisees(): void {
		this._loading = true;
		this.selectionZoneService.rechercherSitesEtZonesAutorises(this.loginUtilisateurConnecte)
			.pipe(finalize(() => this._loading = false))
			.subscribe(sitesZones => {
				// récupère les sites et zones autorisées pour l'utilisateur connecté et reformat pour le composant dropdown avec groupes
				const groupesZones: SelectItemGroup[] = [];

				sitesZones.forEach((siteZone) => {
					groupesZones.push({
						label: siteZone.nom,
						value: siteZone.id,
						items: siteZone.zones.map(zone => {
							return {label: zone.nom, value: zone.id};
						})
					});

					if (this.mode() === Mode.Selection) {
						// Comme on est en mode sélection, on prend la zone par défaut du site s'il y en a une. C'est le cas pour l'écran de sélection d'un bureau
						const zoneParDefaut = siteZone.zones.find(zone => zone.zoneParDefaut);
						if (siteZone.zones.find(zone => zone.zoneParDefaut) !== undefined) {
							this._zoneSelectionneeId = zoneParDefaut.id;
						}
					}
				});

				this.groupesZones = groupesZones;

				// En mode sélection s'il n'y a qu'une seule zone alors, on la sélectionne par défaut.
				if (this.mode() === Mode.Selection && this._zoneSelectionneeId == null && this.groupesZones.length === 1) {
					this._zoneSelectionneeId = this.groupesZones[0].items[0].value;
				}
			});
	}

	/**
	 * Notifie le changement de zone sélectionnée
	 * @param zoneSelectionneeId l'id de la zone sélectionnée
	 */
	onZoneChanged(zoneSelectionneeId?: number): void {
		if (zoneSelectionneeId !== null) {
			this.notifierZoneSelectionneeId.emit(zoneSelectionneeId);
		}
	}

	protected readonly Mode = Mode;

	clearZoneSelectionee() {
		this._zoneSelectionneeId = null;
		this.notifierZoneSelectionneeId.emit(null);
	}

	determinerLabelDropdown(): string {
		if (this.mode() == Mode.Selection) {
			return this.translateService.instant('SELECTION_ZONE.ZONE');
		}

		return this.translateService.instant('SELECTION_ZONE.ZONE_PAR_DEFAUT');
	}
}
